import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { classicAppUrl, isLocalHost } from '../utils/origins';
import { getStoredAppVariant, setStoredAppVariant } from '../utils/appVariant';

export default function AppChooser({ mode = 'sign-in', children }) {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [showChooser, setShowChooser] = useState(true);
  const [onClassicHost, setOnClassicHost] = useState(false);
  const search = location.search || '';
  const path = mode === 'sign-up' ? '/sign-up' : '/sign-in';
  const classicHref = classicAppUrl(path, search);

  useEffect(() => {
    const host = window.location.hostname;
    if (isLocalHost(host) || host === 'app.getupgraded.ca' || host.startsWith('app.')) {
      setOnClassicHost(true);
      setShowChooser(false);
      setReady(true);
      return;
    }
    const params = new URLSearchParams(location.search);
    const forced = params.get('app');
    if (forced === 'classic') {
      setStoredAppVariant('classic');
      window.location.assign(classicHref);
      return;
    }
    if (forced === 'new') {
      setStoredAppVariant('new');
      setShowChooser(false);
      setReady(true);
      return;
    }
    const stored = getStoredAppVariant();
    if (stored === 'classic') {
      window.location.assign(classicHref);
      return;
    }
    if (stored === 'new') {
      setShowChooser(false);
    }
    setReady(true);
  }, [classicHref, location.search]);

  const chooseNew = () => {
    setStoredAppVariant('new');
    setShowChooser(false);
  };

  const chooseClassic = () => {
    setStoredAppVariant('classic');
    window.location.assign(classicHref);
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  if (!showChooser) {
    return (
      <div className="relative">
        {!onClassicHost ? (
          <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
            <a
              href={classicHref}
              onClick={() => setStoredAppVariant('classic')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Switch to classic app
            </a>
          </div>
        ) : null}
        {children}
      </div>
    );
  }

  const heading = mode === 'sign-up' ? 'Create an account' : 'Sign in';

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-16">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Upgraded</p>
        <h1 className="mt-3 text-xl font-semibold tracking-tight text-gray-900">{heading}</h1>
        <p className="mt-2 text-sm text-gray-600">
          Choose the app you want to use. You can switch later from the sign-in screen.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <button
            type="button"
            onClick={chooseNew}
            className="flex flex-col items-start rounded-xl border border-gray-100 bg-white p-6 text-left hover:border-gray-200 hover:shadow-sm transition-all duration-150"
          >
            <span className="text-sm font-semibold text-gray-900">Continue on getupgraded.ai</span>
            <span className="mt-2 text-sm text-gray-600">
              The new app — grants, savings, and AI upgrades in one place.
            </span>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-900">
              Use new app
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          </button>
          <button
            type="button"
            onClick={chooseClassic}
            className="flex flex-col items-start rounded-xl border border-gray-100 bg-white p-6 text-left hover:border-gray-200 hover:shadow-sm transition-all duration-150"
          >
            <span className="text-sm font-semibold text-gray-900">Open classic app</span>
            <span className="mt-2 text-sm text-gray-600">
              The existing workspace at app.getupgraded.ca, with your current login.
            </span>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-900">
              Use classic app
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
